import { readonly, ref } from 'vue'
import { normalizeRankJobFilter } from '@/core/rank-job-family'
import { RANK_MINIMUM_DURATION_MS, toRankExpPoint } from '@/core/efficiency-segment-tracker'
import {
  orderedRankSources,
  isRankPresetId,
  RANK_SOURCE_ORDER,
  type AppSetting,
  type RankLevelBucketFilter,
  type RankMapItem,
  type RankMapPage,
  type RankMeta,
  type RankMetaJob,
  type RankQueueItem,
  type RankPresetId,
  type RankSource,
  type RankStatus,
  type RankSubmission,
  type RankSubmissionDraft,
  type RankSyncState,
} from '@/domain/types'
import { db } from '@/services/database'
import { mxdcMapImage } from '@/services/mxdc-url'

const RETRY_BASE_MS = 30_000
const RETRY_MAX_MS = 6 * 60 * 60 * 1_000
const QUEUE_BATCH_SIZE = 10
const RANK_CACHE_TTL_MS = 60_000
const VISITOR_ID_SETTING = 'rankVisitorId'
const LAST_SUCCESS_SETTING = 'rankLastSucceededAt'
const LAST_ERROR_SETTING = 'rankLastErrorAt'
export const RANK_ACCOUNTING_VERSION = 2
export const RANK_PAGE_SIZE = 10
export const RANK_SEARCH_MAX_LENGTH = 80
export const RANK_SEARCH_DEBOUNCE_MS = 280

type RankApiErrorCode = 'network' | 'response' | 'invalid-data'

interface EtagCacheEntry<T> {
  readonly value: T
  readonly etag: string | null
  readonly fetchedAt: number
}

const responseCache = new Map<string, EtagCacheEntry<unknown>>()
const mutableSyncState = ref<RankSyncState>({
  status: 'idle',
  pendingCount: 0,
  lastSucceededAt: null,
  lastErrorAt: null,
})

export const rankSyncState = readonly(mutableSyncState)

let initialized = false
let flushPromise: Promise<void> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null

export class RankApiError extends Error {
  constructor(
    readonly code: RankApiErrorCode,
    message: string,
    readonly status: number | null = null,
  ) {
    super(message)
    this.name = 'RankApiError'
  }
}

export function createRankQueueItems(sessionId: string, submissions: readonly RankSubmissionDraft[]): RankQueueItem[] {
  const now = Date.now()
  return submissions.filter(meetsRankDurationMinimum).map((submission) => ({
    id: submission.submissionId,
    sessionId,
    accountingVersion: RANK_ACCOUNTING_VERSION,
    submission: canonicalRankDraft(submission),
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    nextAttemptAt: now,
    status: 'pending',
  }))
}

function canonicalRankDraft(submission: RankSubmissionDraft): RankSubmissionDraft {
  if (!isRankPresetId(submission.presetId)) throw new RankApiError('invalid-data', 'Unsupported ranking preset')
  return {
    submissionId: submission.submissionId,
    presetId: submission.presetId,
    presetVersion: submission.presetVersion,
    mapName: submission.mapName,
    levelBucket: submission.levelBucket,
    ...(submission.jobKey !== undefined ? { jobKey: submission.jobKey } : {}),
    ...(submission.jobName !== undefined ? { jobName: submission.jobName } : {}),
    startedAt: submission.startedAt,
    endedAt: submission.endedAt,
    exp: {
      gain: submission.exp.gain,
      trustedDurationMs: submission.exp.trustedDurationMs,
      start: toRankExpPoint(submission.exp.start),
      end: toRankExpPoint(submission.exp.end),
    },
  }
}

function meetsRankDurationMinimum(submission: RankSubmissionDraft): boolean {
  return submission.endedAt - submission.startedAt >= RANK_MINIMUM_DURATION_MS
    && submission.exp.trustedDurationMs >= RANK_MINIMUM_DURATION_MS
}

export function initializeRankSync(): void {
  if (initialized) return
  initialized = true
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  void prepareQueue()
    .then(() => flushRankQueue())
    .catch(markSyncFailure)
}

export function notifyRankQueueChanged(): void {
  void refreshRankSyncState().catch(markSyncFailure)
  void flushRankQueue().catch(markSyncFailure)
}

export async function refreshRankSyncState(): Promise<void> {
  const [pendingCount, lastSucceededAt, lastErrorAt, blockedCount] = await Promise.all([
    db.rankQueue.count(),
    readNumberSetting(LAST_SUCCESS_SETTING),
    readNumberSetting(LAST_ERROR_SETTING),
    db.rankQueue.where('status').equals('configuration-blocked').count(),
  ])
  mutableSyncState.value = {
    status: blockedCount > 0
      ? 'configuration-error'
      : pendingCount > 0 && !isOnline()
        ? 'waiting-network'
        : flushPromise
          ? 'syncing'
          : 'idle',
    pendingCount,
    lastSucceededAt,
    lastErrorAt,
  }
}

export async function flushRankQueue(): Promise<void> {
  if (flushPromise) return flushPromise
  clearRetryTimer()
  flushPromise = flushDueItems()
    .finally(async () => {
      flushPromise = null
      await refreshRankSyncState()
      await scheduleNextFlush()
    })
  await refreshRankSyncState()
  return flushPromise
}

export async function listRankMaps(options: {
  presetId?: RankPresetId
  levelBucket: RankLevelBucketFilter
  jobKey?: string
  q?: string
  source: RankSource
  status?: RankStatus
  page: number
  pageSize?: number
  signal?: AbortSignal
  force?: boolean
}): Promise<RankMapPage> {
  const presetId = options.presetId ?? 'mxdc'
  if (!isRankPresetId(presetId)) throw new RankApiError('invalid-data', 'Unsupported ranking preset')
  const params = new URLSearchParams({
    presetId,
    metric: 'exp',
    levelBucket: String(options.levelBucket),
    source: options.source,
    status: options.status ?? 'all',
    page: String(options.page),
    pageSize: String(options.pageSize ?? RANK_PAGE_SIZE),
  })
  const jobKey = normalizeRankJobFilter(options.jobKey)
  if (options.jobKey && !jobKey) throw new RankApiError('invalid-data', 'Invalid job filter')
  if (jobKey) params.set('jobKey', jobKey)
  const keyword = options.q?.trim()
  if (keyword) params.set('q', keyword)
  const path = `/api/v1/rank/maps?${params}`
  return requestCached(path, value => parseRankMapPage(value, presetId), options.signal, options.force)
}

export async function getRankMeta(signal?: AbortSignal, force = false): Promise<RankMeta> {
  return requestCached('/api/v1/rank/meta?presetId=mxdc', parseRankMeta, signal, force)
}

export async function getRankHealth(signal?: AbortSignal): Promise<boolean> {
  const response = await fetch('/api/v1/rank/health', { headers: rankHeaders({ accept: 'application/json' }), signal, cache: 'no-store' })
    .catch((caught: unknown) => { throw new RankApiError('network', errorMessage(caught)) })
  if (response.status !== 200) return false
  const body: unknown = await response.json().catch(() => null)
  signal?.throwIfAborted()
  return body !== null && typeof body === 'object' && 'ok' in body && body.ok === true
}

async function prepareQueue(): Promise<void> {
  const items = await db.rankQueue.toArray()
  const incompatible = items.filter((item) => item.accountingVersion !== RANK_ACCOUNTING_VERSION).map((item) => item.id)
  const blocked = items.filter((item) => item.accountingVersion === RANK_ACCOUNTING_VERSION && item.status === 'configuration-blocked')
  await db.transaction('rw', db.rankQueue, async () => {
    if (incompatible.length) await db.rankQueue.bulkDelete(incompatible)
    if (blocked.length) {
      const now = Date.now()
      await db.rankQueue.bulkPut(blocked.map((item) => ({ ...item, status: 'pending', nextAttemptAt: now, updatedAt: now })))
    }
  })
  await refreshRankSyncState()
}

async function flushDueItems(): Promise<void> {
  if (!isOnline()) return
  while (isOnline()) {
    const due = (await db.rankQueue.where('status').equals('pending').sortBy('nextAttemptAt'))
      .filter((item) => item.nextAttemptAt <= Date.now())
      .slice(0, QUEUE_BATCH_SIZE)
    if (!due.length) return
    const visitorId = await getVisitorId()
    for (const item of due) {
      if (item.accountingVersion !== RANK_ACCOUNTING_VERSION || !meetsRankDurationMinimum(item.submission)) {
        await db.rankQueue.delete(item.id)
        continue
      }
      try {
        await submitRank({ ...canonicalRankDraft(item.submission), visitorId })
        await db.transaction('rw', db.rankQueue, db.settings, async () => {
          await db.rankQueue.delete(item.id)
          await writeNumberSetting(LAST_SUCCESS_SETTING, Date.now())
        })
      } catch (caught) {
        await writeNumberSetting(LAST_ERROR_SETTING, Date.now())
        if (isConfigurationError(caught)) {
          await db.rankQueue.update(item.id, {
            status: 'configuration-blocked',
            updatedAt: Date.now(),
            lastError: errorMessage(caught).slice(0, 240),
          })
          return
        }
        if (isPermanentSubmissionError(caught)) {
          await db.rankQueue.delete(item.id)
          continue
        }
        await rescheduleQueueItem(item, caught)
      }
    }
  }
}

async function submitRank(submission: RankSubmission): Promise<void> {
  const response = await requestJson('/api/v1/rank/submissions', {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    // Both servers use protocol version 9, independently of local recognition versions.
    body: JSON.stringify({ ...canonicalRankDraft(submission), visitorId: submission.visitorId, presetVersion: 9 }),
  })
  const body = asRecord(response, 'submission response')
  if (body.accepted !== true || body.submissionId !== submission.submissionId) {
    throw new RankApiError('invalid-data', 'Invalid submission acknowledgement')
  }
}

async function getVisitorId(): Promise<string> {
  const existing = await db.settings.get(VISITOR_ID_SETTING) as AppSetting<unknown> | undefined
  if (typeof existing?.value === 'string' && existing.value.length >= 16 && existing.value.length <= 128) return existing.value
  const value = crypto.randomUUID()
  await db.settings.put({ key: VISITOR_ID_SETTING, value, updatedAt: Date.now() })
  return value
}

async function requestCached<T>(
  path: string,
  parser: (value: unknown) => T,
  signal?: AbortSignal,
  force = false,
): Promise<T> {
  const cached = responseCache.get(path) as EtagCacheEntry<T> | undefined
  if (!force && cached && Date.now() - cached.fetchedAt < RANK_CACHE_TTL_MS) return cached.value
  const headers = rankHeaders({ accept: 'application/json' })
  if (cached?.etag) headers.set('if-none-match', cached.etag)
  const response = await fetch(path, { headers, signal })
    .catch((caught: unknown) => {
      if (caught instanceof DOMException && caught.name === 'AbortError') throw caught
      throw new RankApiError('network', errorMessage(caught))
    })
  if (response.status === 304 && cached) {
    responseCache.set(path, { ...cached, fetchedAt: Date.now() })
    return cached.value
  }
  if (!response.ok) throw await responseError(response)
  const value = parser(await response.json().catch(() => {
    throw new RankApiError('invalid-data', 'Response is not valid JSON')
  }))
  responseCache.set(path, { value, etag: response.headers.get('etag'), fetchedAt: Date.now() })
  return value
}

async function requestJson(path: string, init: RequestInit): Promise<unknown> {
  const response = await fetch(path, { ...init, headers: rankHeaders(init.headers) }).catch((caught: unknown) => {
    throw new RankApiError('network', errorMessage(caught))
  })
  if (!response.ok) throw await responseError(response)
  return response.json().catch(() => { throw new RankApiError('invalid-data', 'Response is not valid JSON') })
}

function rankHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init)
  const key = import.meta.env.VITE_RANK_API_KEY?.trim()
  if (key) headers.set('authorization', `Bearer ${key}`)
  return headers
}

async function responseError(response: Response): Promise<RankApiError> {
  const detail = await response.text().catch(() => '')
  return new RankApiError('response', `${response.status} ${detail.slice(0, 160)}`.trim(), response.status)
}

function parseRankMapPage(value: unknown, presetId: RankPresetId): RankMapPage {
  const body = asRecord(value, 'rank page')
  if (body.presetId !== undefined && body.presetId !== presetId) throw new RankApiError('invalid-data', 'Unexpected ranking preset')
  const formal = asRecord(body.formal, 'formal ranking')
  const accumulating = asRecord(body.accumulating, 'accumulating ranking')
  return {
    ...(body.status === undefined ? {} : { status: parseRankStatus(body.status) }),
    ...(body.q === undefined ? {} : { q: stringValue(body.q, 'q') }),
    formal: {
      items: asArray(formal.items, 'formal items').map(value => parseRankMapItem(value, presetId)),
      page: positiveInteger(formal.page, 'formal page'),
      pageSize: positiveInteger(formal.pageSize, 'formal page size'),
      total: nonNegativeInteger(formal.total, 'formal total'),
    },
    accumulating: {
      items: asArray(accumulating.items, 'accumulating items').map(value => parseRankMapItem(value, presetId)),
      ...(accumulating.page === undefined ? {} : { page: positiveInteger(accumulating.page, 'accumulating page') }),
      ...(accumulating.pageSize === undefined ? {} : { pageSize: positiveInteger(accumulating.pageSize, 'accumulating page size') }),
      total: nonNegativeInteger(accumulating.total, 'accumulating total'),
    },
    generatedAt: nonNegativeNumber(body.generatedAt, 'generatedAt'),
  }
}

function parseRankMapItem(value: unknown, presetId: RankPresetId): RankMapItem {
  const item = asRecord(value, 'rank item')
  const percentiles = asRecord(item.percentiles, 'rank percentiles')
  const sources = asRecord(item.sources, 'rank sources')
  const rank = item.rank === null ? null : positiveInteger(item.rank, 'rank')
  if (item.presetId !== presetId) throw new RankApiError('invalid-data', 'Unexpected ranking preset')
  if (item.metric !== 'exp') throw new RankApiError('invalid-data', 'Unexpected ranking metric')
  const mapId = item.mapId === null || item.mapId === undefined ? null : nonNegativeInteger(item.mapId, 'mapId')
  return {
    rank,
    presetId,
    mapName: nonEmptyString(item.mapName, 'mapName'),
    mapKey: nonEmptyString(item.mapKey, 'mapKey'),
    mapId,
    mapIcon: typeof item.mapIcon === 'string' ? mxdcMapImage(item.mapIcon) : '',
    mapDetailUrl: presetId === 'mxdc' ? parseMapDetailUrl(item.mapDetailUrl, mapId) : null,
    levelBucket: nonNegativeInteger(item.levelBucket, 'levelBucket'),
    metric: 'exp',
    jobKey: stringValue(item.jobKey, 'jobKey'),
    userCount: nonNegativeInteger(item.userCount, 'userCount'),
    totalTrustedDurationMs: nonNegativeNumber(item.totalTrustedDurationMs, 'totalTrustedDurationMs'),
    percentiles: {
      p25: nonNegativeNumber(percentiles.p25, 'p25'),
      p50: nonNegativeNumber(percentiles.p50, 'p50'),
      p75: nonNegativeNumber(percentiles.p75, 'p75'),
      p90: nonNegativeNumber(percentiles.p90, 'p90'),
    },
    sources: {
      mxdc: nonNegativeInteger(sources.mxdc, 'sources.mxdc'),
      fengji: nonNegativeInteger(sources.fengji, 'sources.fengji'),
    },
    qualified: typeof item.qualified === 'boolean' ? item.qualified : rank !== null,
    updatedAt: nonNegativeNumber(item.updatedAt, 'updatedAt'),
  }
}

function parseRankMeta(value: unknown): RankMeta {
  const body = asRecord(value, 'rank metadata')
  if (body.presetId !== 'mxdc') throw new RankApiError('invalid-data', 'Unexpected ranking preset')
  if (body.supportsAllLevelBuckets !== undefined && typeof body.supportsAllLevelBuckets !== 'boolean') {
    throw new RankApiError('invalid-data', 'Invalid all-level ranking capability')
  }
  if (body.supportsSearch !== undefined && typeof body.supportsSearch !== 'boolean') {
    throw new RankApiError('invalid-data', 'Invalid ranking search capability')
  }
  if (body.supportsMapDetails !== undefined && typeof body.supportsMapDetails !== 'boolean') {
    throw new RankApiError('invalid-data', 'Invalid map detail capability')
  }
  const dropCategories = body.mapDetailDropCategories === undefined
    ? undefined
    : asArray(body.mapDetailDropCategories, 'mapDetailDropCategories').map((value) => {
      if (value !== 'equipment' && value !== 'scroll') throw new RankApiError('invalid-data', 'Invalid drop category')
      return value
    })
  const searchMaxLength = body.searchMaxLength
  if (searchMaxLength !== undefined && (typeof searchMaxLength !== 'number' || !Number.isInteger(searchMaxLength) || searchMaxLength <= 0)) {
    throw new RankApiError('invalid-data', 'Invalid ranking search max length')
  }
  const jobs = asArray(body.jobs, 'jobs').map(parseRankMetaJob)
  const sources: readonly RankSource[] = orderedRankSources(body.sources === undefined
    ? RANK_SOURCE_ORDER
    : asArray(body.sources, 'sources').map(parseRankSource))
  const minimumUserSampleDurationMs = body.minimumUserSampleDurationMs
    ?? body.minimumUserTrustedDurationMs
    ?? RANK_MINIMUM_DURATION_MS
  return {
    presetId: 'mxdc',
    ...(body.supportsAllLevelBuckets === undefined ? {} : { supportsAllLevelBuckets: body.supportsAllLevelBuckets }),
    ...(body.supportsSearch === undefined ? {} : { supportsSearch: body.supportsSearch }),
    ...(body.supportsMapDetails === undefined ? {} : { supportsMapDetails: body.supportsMapDetails }),
    ...(dropCategories === undefined ? {} : { mapDetailDropCategories: dropCategories }),
    ...(searchMaxLength === undefined ? {} : { searchMaxLength }),
    levelBuckets: asArray(body.levelBuckets, 'levelBuckets').map((item) => nonNegativeInteger(item, 'level bucket')),
    jobs,
    sources,
    minimumUsers: positiveInteger(body.minimumUsers, 'minimumUsers'),
    minimumTrustedDurationMs: positiveNumber(body.minimumTrustedDurationMs, 'minimumTrustedDurationMs'),
    minimumUserSampleDurationMs: positiveNumber(minimumUserSampleDurationMs, 'minimumUserSampleDurationMs'),
    lastUpdatedAt: body.lastUpdatedAt === null || body.lastUpdatedAt === undefined
      ? null
      : nonNegativeNumber(body.lastUpdatedAt, 'lastUpdatedAt'),
  }
}

function parseRankMetaJob(value: unknown): RankMetaJob {
  const job = asRecord(value, 'job')
  const key = job.key ?? job.jobKey
  const name = job.name ?? job.jobName
  return {
    key: nonEmptyString(key, 'job key'),
    name: nonEmptyString(name, 'job name'),
    submissions: job.submissions === undefined
      ? null
      : nonNegativeInteger(job.submissions, 'job submissions'),
  }
}

function parseRankStatus(value: unknown): RankStatus {
  if (value === 'all' || value === 'formal' || value === 'accumulating') return value
  throw new RankApiError('invalid-data', 'Invalid ranking status')
}

function parseRankSource(value: unknown): RankSource {
  if (value === 'all' || value === 'mxdc' || value === 'fengji') return value
  throw new RankApiError('invalid-data', 'Invalid ranking source')
}

function parseMapDetailUrl(value: unknown, mapId: number | null): string | null {
  if (mapId === null || mapId <= 0 || typeof value !== 'string' || !value.trim()) return null
  try {
    const url = new URL(value, 'https://maple-log.local')
    if (url.origin !== 'https://maple-log.local' || url.pathname !== '/api/v1/rank/map-detail') return null
    if (url.searchParams.get('presetId') !== 'mxdc' || url.searchParams.get('mapId') !== String(mapId)) return null
    for (const key of url.searchParams.keys()) {
      if (key !== 'presetId' && key !== 'mapId') return null
    }
    return `/api/v1/rank/map-detail?presetId=mxdc&mapId=${mapId}`
  } catch {
    return null
  }
}

function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new RankApiError('invalid-data', `Invalid ${label}`)
  return value as Record<string, unknown>
}

function asArray(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) throw new RankApiError('invalid-data', `Invalid ${label}`)
  return value
}

function nonEmptyString(value: unknown, label: string): string {
  if (typeof value !== 'string' || !value.trim()) throw new RankApiError('invalid-data', `Invalid ${label}`)
  return value
}

function stringValue(value: unknown, label: string): string {
  if (typeof value !== 'string') throw new RankApiError('invalid-data', `Invalid ${label}`)
  return value
}

function nonNegativeNumber(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new RankApiError('invalid-data', `Invalid ${label}`)
  return value
}

function positiveNumber(value: unknown, label: string): number {
  const number = nonNegativeNumber(value, label)
  if (number <= 0) throw new RankApiError('invalid-data', `Invalid ${label}`)
  return number
}

function nonNegativeInteger(value: unknown, label: string): number {
  const number = nonNegativeNumber(value, label)
  if (!Number.isInteger(number)) throw new RankApiError('invalid-data', `Invalid ${label}`)
  return number
}

function positiveInteger(value: unknown, label: string): number {
  const number = nonNegativeInteger(value, label)
  if (number <= 0) throw new RankApiError('invalid-data', `Invalid ${label}`)
  return number
}

function isConfigurationError(caught: unknown): boolean {
  return caught instanceof RankApiError && caught.code === 'response' && [401, 403, 404, 405].includes(caught.status ?? 0)
}

function isPermanentSubmissionError(caught: unknown): boolean {
  return caught instanceof RankApiError && caught.code === 'response' && [400, 413, 422].includes(caught.status ?? 0)
}

async function rescheduleQueueItem(item: RankQueueItem, caught: unknown): Promise<void> {
  const attempts = item.attempts + 1
  const baseDelay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.min(attempts - 1, 10))
  const jitteredDelay = Math.round(baseDelay * (.85 + Math.random() * .3))
  await db.rankQueue.put({
    ...item,
    attempts,
    updatedAt: Date.now(),
    nextAttemptAt: Date.now() + jitteredDelay,
    lastError: errorMessage(caught).slice(0, 240),
  })
}

async function scheduleNextFlush(): Promise<void> {
  clearRetryTimer()
  if (!isOnline()) return
  const pending = await db.rankQueue.where('status').equals('pending').sortBy('nextAttemptAt')
  const next = pending[0]
  if (!next) return
  const delay = Math.max(0, Math.min(RETRY_MAX_MS, next.nextAttemptAt - Date.now()))
  retryTimer = globalThis.setTimeout(() => { void flushRankQueue() }, delay)
}

function clearRetryTimer(): void {
  if (retryTimer !== null) globalThis.clearTimeout(retryTimer)
  retryTimer = null
}

function handleOnline(): void {
  void flushRankQueue().catch(markSyncFailure)
}

function handleOffline(): void {
  clearRetryTimer()
  void refreshRankSyncState().catch(markSyncFailure)
}

function markSyncFailure(): void {
  mutableSyncState.value = {
    ...mutableSyncState.value,
    status: 'idle',
    lastErrorAt: Date.now(),
  }
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

async function readNumberSetting(key: string): Promise<number | null> {
  const setting = await db.settings.get(key) as AppSetting<unknown> | undefined
  return typeof setting?.value === 'number' && Number.isFinite(setting.value) ? setting.value : null
}

async function writeNumberSetting(key: string, value: number): Promise<void> {
  await db.settings.put({ key, value, updatedAt: Date.now() })
}

function errorMessage(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught)
}
